/**
 * Hero / investigation query validation — reject random gibberish before scan dispatch.
 * Mirrors ``backend/utils/query_validator.py``.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  looksLikeSystemCommandOrCode,
  SYSTEM_COMMAND_MESSAGE,
} from '../pages/home/homeSearchUtils';

export const QUERY_GUIDANCE_MESSAGE =
  'Please provide a recognizable name, company, email, or domain.';

const MIN_LEN = 3;
const MAX_LEN = 75;

/** Letters (Unicode) and digits — region-agnostic name / token checks. */
const WORD_CHAR_RE = /[\p{L}\p{N}]/u;
const VOWEL_RE = /[aeiouăâîAEIOUĂÂÎаеёиоуыэюяΑΕΗΙΟΥΩaeiouy]/iu;
const LETTER_RE = /[a-zA-ZăâîșşțţĂÂÎȘŞȚŢ]/;
const PHONE_CHARS_RE = /^[+0-9().\s-]+$/;

const KEYBOARD_WALKS = new Set([
  'qwerty',
  'qwertyuiop',
  'asdf',
  'asdfgh',
  'asdfghjkl',
  'zxcv',
  'zxcvbn',
  'zxcvbnm',
  'abcdef',
  'abcdefg',
  'abcabc',
  'aaa',
  'aaaa',
  'xxxxx',
  'xxxxxx',
  'test',
  'testing',
  'lorem',
  'ipsum',
]);

/**
 * Shannon entropy of a string (bits per character).
 *
 * @param {string} text
 * @returns {number}
 */
export function shannonEntropy(text) {
  const s = String(text || '');
  if (!s.length) return 0;
  const freq = new Map();
  for (const ch of s) {
    freq.set(ch, (freq.get(ch) || 0) + 1);
  }
  let h = 0;
  const n = s.length;
  freq.forEach((count) => {
    const p = count / n;
    h -= p * Math.log2(p);
  });
  return h;
}

/**
 * @param {string} query
 * @returns {boolean}
 */
export function looksLikeEmail(query) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(query || '').trim());
}

/**
 * @param {string} query
 * @returns {boolean}
 */
export function looksLikePhone(query) {
  const q = String(query || '').trim();
  if (!q) return false;
  if (LETTER_RE.test(q)) return false;
  const digits = q.replace(/\D/g, '');
  return digits.length >= 9 && digits.length <= 15 && PHONE_CHARS_RE.test(q);
}

/**
 * @param {string} query
 * @returns {boolean}
 */
export function isPhoneCharBlob(query) {
  const q = String(query || '').trim();
  if (!q || LETTER_RE.test(q)) return false;
  const digits = q.replace(/\D/g, '');
  if (!digits) return false;
  return PHONE_CHARS_RE.test(q);
}

/**
 * Prefer libphonenumber possible-number; fail-open on library edge cases for 9–15 digit shapes.
 *
 * @param {string} query
 * @returns {boolean}
 */
export function phoneNumberIsPossible(query) {
  const q = String(query || '').trim();
  if (!looksLikePhone(q)) return false;
  const digits = q.replace(/\D/g, '');
  const candidates = [
    parsePhoneNumberFromString(q, 'RO'),
    parsePhoneNumberFromString(q, 'US'),
    q.startsWith('+') ? parsePhoneNumberFromString(q) : null,
    digits ? parsePhoneNumberFromString(digits, 'RO') : null,
  ];
  let sawParse = false;
  for (const parsed of candidates) {
    if (!parsed) continue;
    sawParse = true;
    if (parsed.isPossible()) return true;
  }
  return !sawParse;
}

/**
 * Incomplete / random digit soup that must not start a search (e.g. ``07589999``).
 *
 * @param {string} query
 * @returns {boolean}
 */
export function isUnusableDigitOrPhoneBlob(query) {
  if (!isPhoneCharBlob(query)) return false;
  if (!looksLikePhone(query)) return true;
  return !phoneNumberIsPossible(query);
}

/**
 * @param {string} query
 * @returns {boolean}
 */
export function looksLikeDomain(query) {
  const q = String(query || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split('?')[0];
  if (!q || q.includes(' ') || q.includes('@')) return false;
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(q);
}

/**
 * Structured target shapes that should never be treated as gibberish.
 *
 * @param {string} query
 * @param {string} [activeTab]
 * @returns {boolean}
 */
function hasRecognizableStructure(query, activeTab) {
  const q = String(query || '').trim();
  const tab = String(activeTab || '').toLowerCase();
  if (looksLikeEmail(q) || tab === 'email') {
    if (looksLikeEmail(q) || q.includes('@')) return looksLikeEmail(q) || q.includes('@');
  }
  if (looksLikePhone(q) && phoneNumberIsPossible(q)) return true;
  if (looksLikeDomain(q) || /\.[a-z]{2,24}\b/i.test(q)) {
    return true;
  }
  // Multi-token person / company names with spaces or separators.
  if (/[\s,;|/]/.test(q) && WORD_CHAR_RE.test(q)) return true;
  // Short single name token with vowels.
  if (q.length <= 12 && VOWEL_RE.test(q) && /^[\p{L}'.-]+$/u.test(q)) return true;
  // Short all-caps brand tokens (BRD, BCR, IBM).
  if (q.length >= 2 && q.length <= 5 && /^[A-Z]+$/.test(q)) return true;
  return false;
}

/**
 * Ratio of characters that are unusual for investigation queries.
 *
 * @param {string} query
 * @returns {number} 0..1
 */
export function unusualCharRatio(query) {
  const s = String(query || '');
  if (!s.length) return 0;
  let unusual = 0;
  for (const ch of s) {
    if (/[\s.,;:@+\-_()'/]/.test(ch)) continue;
    if (WORD_CHAR_RE.test(ch)) continue;
    unusual += 1;
  }
  return unusual / s.length;
}

/**
 * Long run of consonants or mixed alnum without spaces (keyboard mash).
 *
 * @param {string} query
 * @returns {boolean}
 */
export function hasGibberishToken(query) {
  const tokens = String(query || '')
    .trim()
    .split(/[\s,;|/]+/)
    .filter(Boolean);
  for (const tok of tokens) {
    if (tok.includes('@') || tok.includes('.')) continue;
    const letters = tok.replace(/[^\p{L}]/gu, '');
    if (letters.length >= 8 && !VOWEL_RE.test(letters)) return true;
    // Mixed letter+digit soup without separators: a7Kx9Qm2…
    if (tok.length >= 10 && /[a-zA-Z]/.test(tok) && /\d/.test(tok) && !/[.\-_@+]/.test(tok)) {
      return true;
    }
    // High unique-char density on a single long blob.
    if (tok.length >= 14 && !/[\s.]/.test(tok)) {
      const uniq = new Set(tok.toLowerCase()).size;
      if (uniq / tok.length >= 0.75 && shannonEntropy(tok) >= 3.6) return true;
    }
  }
  return false;
}

/**
 * Placeholder / keyboard-walk / repeated-char single tokens.
 *
 * @param {string} query
 * @returns {boolean}
 */
export function isWeakSingleToken(query) {
  const q = String(query || '').trim();
  if (!q || /\s/.test(q) || q.includes('@') || looksLikeDomain(q) || looksLikePhone(q)) {
    return false;
  }
  if (isPhoneCharBlob(q)) return false;
  const low = q.toLowerCase();
  if (KEYBOARD_WALKS.has(low)) return true;
  const letters = low.replace(/[^a-zA-ZăâîșşțţĂÂÎȘŞȚŢ]/g, '');
  if (letters.length >= 3 && new Set(letters).size === 1) return true;
  if (letters.length >= 5 && new Set(letters).size <= 2 && !VOWEL_RE.test(letters)) return true;
  return false;
}

/**
 * Validate an investigation / hero query before dispatch.
 *
 * @param {string} query
 * @param {string} [activeTab] person | email | phone | company
 * @returns {{ isValid: boolean, reason: string|null, message: string|null }}
 */
export function validateInvestigationQuery(query, activeTab = '') {
  const q = String(query || '').trim();
  if (!q) {
    return { isValid: false, reason: 'EMPTY', message: null };
  }
  if (looksLikeSystemCommandOrCode(q)) {
    return {
      isValid: false,
      reason: 'INVALID_SYNTAX',
      message: SYSTEM_COMMAND_MESSAGE,
    };
  }
  if (q.length < MIN_LEN) {
    return {
      isValid: false,
      reason: 'TOO_SHORT',
      message: QUERY_GUIDANCE_MESSAGE,
    };
  }
  if (q.length > MAX_LEN) {
    return {
      isValid: false,
      reason: 'TOO_LONG',
      message: QUERY_GUIDANCE_MESSAGE,
    };
  }

  const tab = String(activeTab || '').toLowerCase();

  if (isUnusableDigitOrPhoneBlob(q)) {
    return {
      isValid: false,
      reason: 'UNRECOGNIZED_FORMAT',
      message: QUERY_GUIDANCE_MESSAGE,
    };
  }

  if (isWeakSingleToken(q)) {
    return {
      isValid: false,
      reason: 'UNRECOGNIZED_FORMAT',
      message: QUERY_GUIDANCE_MESSAGE,
    };
  }

  if (tab === 'phone') {
    if (looksLikePhone(q) && phoneNumberIsPossible(q)) {
      return { isValid: true, reason: null, message: null };
    }
    if (isPhoneCharBlob(q)) {
      return {
        isValid: false,
        reason: 'UNRECOGNIZED_FORMAT',
        message: QUERY_GUIDANCE_MESSAGE,
      };
    }
  }
  if (tab === 'email' && looksLikeEmail(q)) {
    return { isValid: true, reason: null, message: null };
  }
  if ((tab === 'company' || tab === 'person') && hasRecognizableStructure(q, tab)) {
    if (!hasGibberishToken(q) && unusualCharRatio(q) < 0.25) {
      return { isValid: true, reason: null, message: null };
    }
  }

  if (hasRecognizableStructure(q, tab) && !hasGibberishToken(q) && unusualCharRatio(q) < 0.25) {
    return { isValid: true, reason: null, message: null };
  }

  const singleBlob = !/[\s@]/.test(q) && !looksLikeDomain(q) && !looksLikePhone(q);
  const entropy = shannonEntropy(q);
  const unusual = unusualCharRatio(q);
  const gibberishToken = hasGibberishToken(q);

  const looksNoise =
    (singleBlob && q.length >= 10 && entropy >= 3.5) ||
    (singleBlob && q.length >= 8 && !VOWEL_RE.test(q.replace(/[^a-zA-ZăâîșşțţĂÂÎȘŞȚŢ]/g, ''))) ||
    unusual >= 0.3 ||
    gibberishToken ||
    (singleBlob && q.length >= 12 && /[a-zA-Z]/.test(q) && /\d/.test(q)) ||
    (singleBlob && !hasRecognizableStructure(q, tab));

  if (looksNoise) {
    return {
      isValid: false,
      reason: 'UNRECOGNIZED_FORMAT',
      message: QUERY_GUIDANCE_MESSAGE,
    };
  }

  return { isValid: true, reason: null, message: null };
}
